import React, { useState } from 'react';
import { get } from 'lodash';
import { faLocationArrow } from '@fortawesome/free-solid-svg-icons/faLocationArrow';
import { faUsersLine } from '@fortawesome/free-solid-svg-icons/faUsersLine';

import ErrorBoundary from 'buyside/components/presentation/ErrorBoundary/errorBoundary';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import FundProfileBanner from './FundProfileBanner/fundProfileBanner';
import FundProfileAdminDashboard from './FundProfileAdminDashboard/fundProfileAdminDashboard';
import FundProfileAllocatorDashboard from './FundProfileAllocatorDashboard/fundProfileAllocatorDashboard';
import FundProfileCardIntroRequest from 'buyside/components/presentation/FundProfileCard/FundProfileCardIntroRequest/fundProfileCardIntroRequest';
import PageNavigator from 'buyside/components/presentation/PageNavigator/pageNavigator';
import FundProfileSections from './FundProfileSection/FundProfileSections';
import FundDisclaimerModal from './FundDisclaimerModal/fundDisclaimerModal';
import ProfileHeader, {
  PROFILE_TYPES
} from 'buyside/components/presentation/ProfileHeader/profileHeader';

import {
  SECTIONS,
  MINIMUM_ATTRIBUTES,
  COMMUNITY,
  ALLOCATOR,
  ADMIN,
  FILTERED_ALLOCATOR,
  TITLES
} from 'buyside/util/fundProfileConstants';
import { formatToPercentage } from 'buyside/util/textHelpers';

import styles from './fundProfileShowPageStyles';

const FundProfileShowPage = ({
  loading,
  currentUser,
  fundProfile,
  acceptFundDisclaimer,
  updateBackgroundImage,
  publishProfile,
  sendMessage,
  createAuditRecord,
  updateFundProfile,
  shrink
}) => {
  const [viewAsMode, setViewAsMode] = useState(false);
  const [viewingAs, setViewingAs] = useState(COMMUNITY);

  if (loading) return <LoadingSpinner size="large" />;

  // helpers
  const calculateViewerRole = (isFundAdmin, isAllocator, viewAsMode, viewingAs) => {
    if (isFundAdmin) {
      return viewAsMode ? viewingAs : ADMIN;
    } else {
      return isAllocator ? ALLOCATOR : COMMUNITY;
    }
  };

  const calculateRoles = (isFundAdmin, isAllocator, viewAsMode, viewingAs) => {
    const viewerRole = calculateViewerRole(isFundAdmin, isAllocator, viewAsMode, viewingAs);

    const roleIsAdmin = viewerRole === ADMIN;
    const roleIsAllocator = viewerRole === ALLOCATOR;
    const roleIsCommunity = viewerRole === COMMUNITY;
    return { roleIsAdmin, roleIsAllocator, roleIsCommunity };
  };

  const toggleViewAsMode = () => setViewAsMode(!viewAsMode);
  const viewAs = userType => setViewingAs(userType);

  const {
    isFundAdmin,
    isAllocator,
    currentEmployerName,
    acceptedFundDisclaimer,
    isAdmin,
    canViewUserDetails
  } = currentUser;
  const {
    id: fundProfileId,
    currentEmployees,
    fundAdminIds,
    status,
    updatedAt,
    introRequestsLeft,
    introRequestPeriodEnd,
    pendingIntroRequest,
    vanityLink,
    name: fundName,
    excludedSections,
    currentUserOnBlocklist,
    currentUserViewCount,
    currentUserHasMessagedFund,
    aumRange,
    focus,
    followerCount,
    foundingYear,
    fundType,
    lastReturn,
    location,
    sharpeRatio,
    strategy
  } = fundProfile;
  const sectionCompletenessPercents = JSON.parse(fundProfile.sectionCompletenessPercents);
  const sectionVisibilities = JSON.parse(fundProfile.sectionVisibilities);

  const { roleIsAdmin, roleIsCommunity } = calculateRoles(
    isFundAdmin,
    isAllocator,
    viewAsMode,
    viewingAs
  );

  const isEmpty = section => {
    const attrExists = attr =>
      Array.isArray(fundProfile[attr]) ? !!fundProfile[attr].length : !!fundProfile[attr];
    return !MINIMUM_ATTRIBUTES[section].some(attr => attrExists(attr));
  };

  const calculateVisibleSections = () => {
    // If a section is empty but has a completeness percent, it is confidential and we want to tease rather than hide it
    // If a section is not empty but does not have a completeness percent, we still want to show it
    const isHidden = section =>
      !roleIsAdmin && isEmpty(section) && !sectionCompletenessPercents[section];
    // if a section is blocklisted, a blocklisted allocator cannot see the section at all
    const isBlocklisted = section =>
      isAllocator && currentUserOnBlocklist && sectionVisibilities[section] === FILTERED_ALLOCATOR;

    const visibleSections = SECTIONS.filter(
      section => !isHidden(section) && !isBlocklisted(section)
    );
    return { visibleSections };
  };

  const sectionProps = section => ({
    roleIsAdmin,
    roleIsCommunity,
    fundProfile,
    sectionEmpty: isEmpty(section),
    sectionVisibility: sectionVisibilities[section],
    sectionCompletenessPercent: sectionCompletenessPercents[section],
    sectionExcluded: excludedSections.includes(section),
    updateFundProfile,
    createAuditRecord,
    shrink
  });

  const { visibleSections } = calculateVisibleSections();

  const mainDatapoints = [
    followerCount > 0
      ? {
          icon: faUsersLine,
          title: `${followerCount} Followers`
        }
      : null,
    location
      ? {
          icon: faLocationArrow,
          title: location
        }
      : null
  ];
  const datapoints = [
    {
      title: 'Fund Type',
      value: fundType || 'N/A'
    },
    { title: 'Strategy', value: strategy || 'N/A' },
    {
      title: 'LTM • YTD',
      value: `${formatToPercentage(get(lastReturn, 'ltm'))} • ${formatToPercentage(
        get(lastReturn, 'ytd')
      )}`
    },
    {
      title: `Since ${foundingYear}`,
      value: formatToPercentage(get(lastReturn, 'sinceInception'))
    },
    {
      title: 'Focus',
      value: focus || 'N/A'
    },
    {
      title: 'Sharpe',
      value: sharpeRatio || 'N/A'
    },
    {
      title: 'AUM Range',
      value: aumRange || 'N/A'
    }
  ];

  return (
    <>
      <ProfileHeader
        datapoints={datapoints}
        displayName={fundProfile.name}
        mainDatapoints={mainDatapoints}
        profile={fundProfile}
        profileImageUrl={fundProfile.logoProfileUrl}
        profileType={PROFILE_TYPES.FUND}
        recommendation={
          isAllocator && fundProfile.fundRecommendation && !fundProfile.fundRecommendation.hide
            ? fundProfile.fundRecommendation
            : null
        }
        roleIsAdmin={roleIsAdmin}
        saveBackgroundImage={updateBackgroundImage}
        shrink={shrink}
        summaryHtml={fundProfile.description || null}
        updateCard={({ fundProfile }) => updateFundProfile({ fundProfile, section: 'card' })}
      />
      <PageNavigator
        fullWidth={!shrink}
        items={visibleSections.map(section => ({
          label: TITLES[section],
          id: section
        }))}
      />
      {viewAsMode && (
        <div className={styles.profileBanner}>
          <FundProfileBanner
            status={status}
            viewAsMode={viewAsMode}
            viewingAs={viewingAs}
            toggleViewAsMode={toggleViewAsMode}
            viewAs={viewAs}
          />
        </div>
      )}
      {roleIsAdmin && (
        <FundProfileAdminDashboard
          currentEmployees={currentEmployees}
          currentUserId={currentUser.id}
          fundAdminIds={fundAdminIds}
          fundProfileId={fundProfileId}
          introRequestPeriodEnd={introRequestPeriodEnd}
          introRequestsLeft={introRequestsLeft}
          isAdmin={isAdmin}
          publishProfile={publishProfile}
          status={status}
          toggleViewAsMode={toggleViewAsMode}
          updatedAt={updatedAt}
          vanityLink={vanityLink}
        />
      )}
      {isAllocator && (
        <>
          {pendingIntroRequest && (
            <FundProfileCardIntroRequest
              fundName={fundName}
              pendingIntroRequest={pendingIntroRequest}
            />
          )}
          <FundProfileAllocatorDashboard
            currentEmployerName={currentEmployerName}
            fundName={fundName}
            sendMessage={sendMessage}
            viewCount={currentUserHasMessagedFund ? 0 : currentUserViewCount}
          />
        </>
      )}
      <ErrorBoundary pageName="fund profile sections">
        <FundProfileSections
          fundProfileId={fundProfileId}
          sectionProps={sectionProps}
          showUserDetails={canViewUserDetails}
          visibleSections={visibleSections}
        />
      </ErrorBoundary>
      <footer>
        <small>
          <strong>Disclaimer</strong>: The information presented in this profile has been prepared
          solely for informational purposes and does not constitute an offer to sell or a
          solicitation of an offer to buy interests or any other securities or to participate in any
          trading strategy.
        </small>
      </footer>
      {isAllocator && !acceptedFundDisclaimer && (
        <FundDisclaimerModal acceptFundDisclaimer={acceptFundDisclaimer} />
      )}
    </>
  );
};

export default FundProfileShowPage;
